/**
 * repairParentLinkedStudentIds — one-shot repair utility
 *
 * Reconciles `users/{userId}.linkedStudentIds` with the `linkedChildren`
 * array held in parentStore. Writes when the field is absent or differs.
 *
 * Called from parentStore.setInitialData (after linkedChildren is seeded)
 * and again from the consolidated listener after the first successful snapshot.
 *
 * @param {string} userId - Parent user ID
 * @param {Array} linkedChildren - Array of linked child objects with `studentId` field
 * @returns {Promise<{repaired: boolean, before: string[]|undefined, after: string[]}>}
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export async function repairParentLinkedStudentIds(userId, linkedChildren) {
  const expected = linkedChildren.map(c => c.studentId);

  const userDocRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userDocRef);

  const current = userSnap.data()?.linkedStudentIds;

  // Check if already in sync (set equality)
  const needsRepair = current === undefined ||
    !arraysEqualAsSet(current, expected);

  if (!needsRepair) {
    return {
      repaired: false,
      before: current,
      after: expected
    };
  }

  // Write the reconciled array
  await setDoc(
    userDocRef,
    {
      linkedStudentIds: expected,
      parent: true,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  return {
    repaired: true,
    before: current,
    after: expected
  };
}

/**
 * Compare two arrays as sets (order-independent, no duplicates considered).
 */
function arraysEqualAsSet(a, b) {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size !== setB.size) return false;
  for (const item of setA) {
    if (!setB.has(item)) return false;
  }
  return true;
}
